import { OSId } from '@ps/platform-message-framework/common/OSId';
import { canUseDOM } from '@ps/web-core-utils';
import compact from 'lodash/compact';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { select } from '@swf/core/store';

const getLockedElements = () =>
  canUseDOM ? compact(document.body.dataset.lockedElements?.split(',') ?? []) : [];

const useBodyScrollLock = (name: string, isLocked: boolean) => {
  const scrollPosition = useRef(canUseDOM ? window.scrollY : 0);
  const os = useSelector(select.device.getOS);
  const isIOS = os === OSId.I_OS;
  const isPad = canUseDOM
    ? os === OSId.MAC && navigator.maxTouchPoints && navigator.maxTouchPoints > 1
    : false;
  const currentModal = useSelector(select.modal.getCurrent);
  const [isBodyScrollLocked, setIsBodyScrollLocked] = useState(isLocked);

  const lock = useCallback(() => {
    if (canUseDOM) {
      document.body.dataset.lockedElements = [...getLockedElements(), name].join(',');
      document.body.style.overflow = 'hidden';
      if (isIOS || isPad) {
        document.body.style.position = 'fixed';
        document.body.style.left = '0px';
        document.body.style.right = '0px';
        document.body.style.top = `-${scrollPosition.current}px`;
      }
    }
  }, [name, isIOS, isPad]);

  const unlock = useCallback(() => {
    if (canUseDOM) {
      if (getLockedElements().length > 1) {
        document.body.dataset.lockedElements = getLockedElements()
          .filter((el) => el !== name)
          .join(',');
        return;
      }

      document.body.dataset.lockedElements = '';
      document.body.style.removeProperty('overflow');

      document.body.style.removeProperty('position');
      document.body.style.removeProperty('left');
      document.body.style.removeProperty('right');
      document.body.style.removeProperty('top');
    }
  }, [name]);

  useEffect(() => setIsBodyScrollLocked(isLocked), [isLocked]);

  useEffect(() => {
    if (isBodyScrollLocked) {
      scrollPosition.current = Math.max(
        window.scrollY,
        document.body.scrollTop,
        document.documentElement.scrollTop,
        document.scrollingElement?.scrollTop ?? 0
      );
      lock();
    } else {
      unlock();
      window.scrollTo(0, scrollPosition.current);
    }
  }, [name, isBodyScrollLocked, lock, unlock]);

  // Toggle locked state based on account modal
  useEffect(() => {
    if (!isLocked) return;
    if (!currentModal) return setIsBodyScrollLocked(true);
    if (currentModal?.path.startsWith('/account')) return setIsBodyScrollLocked(false);
  }, [currentModal, isLocked]);

  useEffect(() => () => unlock(), [unlock]);
};

export default useBodyScrollLock;
